import type { FileStat, FileSystemAdapter } from '@signage/player-core';
import type { ByteStorage } from '@signage/player-ui/host/database';
import { deleteFile, getFile, listPaths, moveFile, putFile } from './idb.js';
import { Sha256 } from './sha256.js';

/**
 * The player-core filesystem, kept in IndexedDB.
 *
 * Paths are plain strings ("media/<sha256>"); directories are implied by the slashes, so
 * creating one costs nothing. Downloads are appended in chunks, each written as soon as it is
 * complete, so an interrupted download resumes from the last chunk rather than from zero.
 */

/** How much of a download is gathered before it is written: big enough to be quick, small
 *  enough that a power cut loses little. */
const WRITE_CHUNK_BYTES = 4 * 1024 * 1024;
/** How much of a file is read at a time while hashing it. */
const HASH_SLICE_BYTES = 2 * 1024 * 1024;

async function blobBytes(blob: Blob): Promise<Uint8Array> {
  // Blob.arrayBuffer() is missing from the oldest signage browsers; FileReader is everywhere.
  if (typeof blob.arrayBuffer === 'function') return new Uint8Array(await blob.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('Cannot read file'));
    reader.readAsArrayBuffer(blob);
  });
}

export class WebFileSystem implements FileSystemAdapter, ByteStorage {
  async ensureDir(): Promise<void> {
    // Nothing to create: a directory exists as soon as a file is stored under it.
  }

  async exists(path: string): Promise<boolean> {
    if (await getFile(path)) return true;
    return (await listPaths(`${path}/`)).length > 0;
  }

  async stat(path: string): Promise<FileStat | null> {
    const file = await getFile(path);
    return file ? { size: file.blob.size, mtimeMs: file.mtimeMs } : null;
  }

  async listDir(path: string): Promise<string[]> {
    const prefix = path.endsWith('/') ? path : `${path}/`;
    const names = new Set<string>();
    for (const full of await listPaths(prefix)) {
      const rest = full.slice(prefix.length);
      if (rest) names.add(rest.split('/')[0]!);
    }
    return [...names];
  }

  async remove(path: string): Promise<void> {
    await deleteFile(path);
  }

  async rename(from: string, to: string): Promise<void> {
    await moveFile(from, to);
  }

  async appendStream(path: string, data: AsyncIterable<Uint8Array>): Promise<number> {
    const existing = await getFile(path);
    let blob: Blob = existing?.blob ?? new Blob([]);
    let pending: Uint8Array[] = [];
    let pendingBytes = 0;
    let written = 0;

    const flush = async (): Promise<void> => {
      if (pendingBytes === 0) return;
      blob = new Blob([blob, ...(pending as BlobPart[])]);
      await putFile(path, blob);
      written += pendingBytes;
      pending = [];
      pendingBytes = 0;
    };

    for await (const chunk of data) {
      // Copied: the stream may reuse its buffer for the next chunk.
      pending.push(chunk.slice());
      pendingBytes += chunk.length;
      if (pendingBytes >= WRITE_CHUNK_BYTES) await flush();
    }
    await flush();
    return written;
  }

  async readFile(path: string): Promise<Uint8Array> {
    const file = await getFile(path);
    if (!file) throw new Error(`No such file: ${path}`);
    return blobBytes(file.blob);
  }

  async writeFile(path: string, data: Uint8Array | string): Promise<void> {
    const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    await putFile(path, new Blob([bytes as BlobPart]));
  }

  async hashFile(path: string): Promise<string> {
    const file = await getFile(path);
    if (!file) throw new Error(`No such file: ${path}`);
    const hash = new Sha256();
    for (let offset = 0; offset < file.blob.size; offset += HASH_SLICE_BYTES) {
      hash.update(await blobBytes(file.blob.slice(offset, offset + HASH_SLICE_BYTES)));
    }
    return hash.hex();
  }

  async diskSpace(): Promise<{ freeBytes: number; totalBytes: number }> {
    // What the browser lets this site use, not the whole disk.
    const estimate = await navigator.storage?.estimate?.().catch(() => null);
    const total = estimate?.quota ?? 0;
    return { totalBytes: total, freeBytes: Math.max(0, total - (estimate?.usage ?? 0)) };
  }

  join(...parts: string[]): string {
    return parts
      .filter((part) => part.length > 0)
      .join('/')
      .replace(/\/{2,}/g, '/');
  }

  /* ------------------------------------------------------------ ByteStorage */

  readFileBytes(path: string): Promise<Uint8Array> {
    return this.readFile(path);
  }

  /** One IndexedDB put is atomic: readers see the old database or the new one, never half. */
  writeFileAtomic(path: string, bytes: Uint8Array): Promise<void> {
    return this.writeFile(path, bytes);
  }
}
